import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FileText, Download } from 'lucide-react';
import SEO from '../components/SEO';

const DocumentationPage = ({ onRequestDemo, onRequestQuote }) => {
  const documents = {
    ctrl: [
      { name: 'UL101 Aviation Troubleshooter Datasheet', type: 'PDF', size: '7.5 MB', href: '/docs/ctrl-ul101-aviation-troubleshooter-barfield.pdf' },
      { name: 'Case Study — CFM LEAP-1B Fuel Nozzle Inspection, Boeing 737 MAX', type: 'PDF', size: '3.6 MB', href: '/docs/case-study-cfm-international-leap-1-b-fuel-nozzle-inspection-boeing-737-max.pdf' },
      { name: 'Case Study — Flap Control & Bypass Valves, Boeing 737-700', type: 'PDF', size: '0.7 MB', href: '/docs/case-study-flap-control-and-bypass-valves-boeing-737-700.pdf' },
      { name: 'Case Study — Leading Edge Flap Transmission Actuators, Boeing 737-700', type: 'PDF', size: '0.5 MB', href: '/docs/case-study-leading-edge-flap-transmission-actuators-boeing-737-700-2.pdf' },
      { name: 'Case Study — Pinpointing Nitrogen Leaks, Boeing 777 NLG', type: 'PDF', size: '3.6 MB', href: '/docs/case-study-pinpointing-nitrogen-leaks-in-777-ngl-.pdf' },
      { name: 'Case Study — Trailing Edge Flap Disagree, Boeing 757-200', type: 'PDF', size: '0.5 MB', href: '/docs/case-study-trailing-edge-flap-disagree-boeing-757-200.pdf' },
    ],
    fotric: [
      { name: 'FOTRIC H-Series (H4 / H6) Acoustic Camera Datasheet', type: 'PDF', size: '4.1 MB', href: '/docs/ctrl-systems-h-series-datasheet.pdf' },
    ],
    dalcomm: [
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Documentation — ctrl.aero" description="Product manuals, spec sheets, and case studies for CTRL UL101, FOTRIC acoustic imagers, and Dalcomm aviation headsets." />
      <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/images/img_0235.jpeg"
            alt="Aviation"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Documentation & Resources
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Download product datasheets, user manuals, and technical documentation
          </p>
        </div>
      </section>

      {/* Documentation Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* CTRL Systems */}
          <div className="mb-16">
            <div className="flex items-center mb-8">
              <img 
                src="/images/ctrl-bar-logo-no-border-.png"
                alt="CTRL Systems"
                className="h-12 w-auto mr-4"
              />
              <h2 className="text-3xl font-bold text-[#1e2d3b]">CTRL Systems Documentation</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {documents.ctrl.map((doc, index) => (
                <a
                  key={doc.href}
                  href={doc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="block bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-blue-500 hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  data-testid="documentation-pdf-link"
                >
                  <div className="flex items-start justify-between mb-4">
                    <FileText className="w-10 h-10 text-blue-600" />
                    <Download className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1e2d3b] mb-2 group-hover:text-blue-600 transition-colors">
                    {doc.name}
                  </h3>
                  <div className="flex items-center space-x-3 text-sm text-slate-500">
                    <span>{doc.type}</span>
                    <span>•</span>
                    <span>{doc.size}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* FOTRIC */}
          <div className="mb-16">
            <div className="flex items-center mb-8">
              <img 
                src="/images/fotric-logo-transparent.png"
                alt="FOTRIC"
                className="h-12 w-auto mr-4"
              />
              <h2 className="text-3xl font-bold text-[#1e2d3b]">FOTRIC Documentation</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {documents.fotric.map((doc, index) => (
                <a
                  key={doc.href}
                  href={doc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="block bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-blue-500 hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  data-testid="documentation-pdf-link"
                >
                  <div className="flex items-start justify-between mb-4">
                    <FileText className="w-10 h-10 text-blue-600" />
                    <Download className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1e2d3b] mb-2 group-hover:text-blue-600 transition-colors">
                    {doc.name}
                  </h3>
                  <div className="flex items-center space-x-3 text-sm text-slate-500">
                    <span>{doc.type}</span>
                    <span>•</span>
                    <span>{doc.size}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Dalcomm Tech */}
          {documents.dalcomm.length > 0 && <div>
            <div className="flex items-center mb-8">
              <img 
                src="/images/img_0290.jpeg"
                alt="Dalcomm Tech"
                className="h-12 w-auto mr-4"
              />
              <h2 className="text-3xl font-bold text-[#1e2d3b]">Dalcomm Tech Documentation</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {documents.dalcomm.map((doc, index) => (
                <a
                  key={doc.href}
                  href={doc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="block bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-blue-500 hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  data-testid="documentation-pdf-link"
                >
                  <div className="flex items-start justify-between mb-4">
                    <FileText className="w-10 h-10 text-blue-600" />
                    <Download className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1e2d3b] mb-2 group-hover:text-blue-600 transition-colors">
                    {doc.name}
                  </h3>
                  <div className="flex items-center space-x-3 text-sm text-slate-500">
                    <span>{doc.type}</span>
                    <span>•</span>
                    <span>{doc.size}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>}

          {/* Note about downloads */}
          <div className="mt-16 max-w-3xl mx-auto">
            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
              <h4 className="font-bold text-[#1e2d3b] mb-2">Need Additional Documentation?</h4>
              <p className="text-slate-700">
                If you cannot find the documentation you need, please contact our technical support team. We can provide specialized guides, integration documents, and custom training materials.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DocumentationPage;
